'use client'

import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react'
import { createClient, clearPendingRequests } from '@kairos/supabase/browser'
import type { User } from '@supabase/supabase-js'
import Cookies from 'js-cookie'
import {
  getCachedAuthState,
  setCachedAuthState,
  clearCachedAuthState,
  shouldRevalidateCache,
} from '../session-cache'

interface AuthContextType {
  user: User | null
  accessToken: string | null
  loading: boolean
  isSuperAdmin: boolean
  isCmsUser: boolean
  adminCheckComplete: boolean
  /** True while verifying auth in background (UI should still render) */
  isVerifying: boolean
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  accessToken: null,
  loading: true,
  isSuperAdmin: false,
  isCmsUser: false,
  adminCheckComplete: false,
  isVerifying: true,
  signOut: async () => {},
  refreshUser: async () => {},
})

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Initialize from cache immediately for instant hydration
  const cachedState = useMemo(() => getCachedAuthState(), [])

  const [user, setUser] = useState<User | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [isVerifying, setIsVerifying] = useState(true)
  const [isSuperAdmin, setIsSuperAdmin] = useState(cachedState?.isSuperAdmin ?? false)
  const [isCmsUser, setIsCmsUser] = useState(cachedState?.isCmsUser ?? false)
  const [adminCheckComplete, setAdminCheckComplete] = useState(!!cachedState)

  // Memoize supabase client to prevent recreation on each render
  const supabase = useMemo(() => createClient(), [])

  // Helper to add timeout to any promise
  const withTimeout = useCallback(<T,>(promise: Promise<T>, ms: number, fallback: T): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
    ])
  }, [])

  // Run admin/CMS checks in background (non-blocking)
  const runAdminChecks = useCallback(
    async (userId: string) => {
      try {
        const adminPromise = Promise.resolve(
          supabase.rpc('get_user_admin_status', { user_id: userId }).then((r) => r.data)
        )
        const cmsPromise = Promise.resolve(
          supabase.rpc('get_user_cms_status', { p_user_id: userId }).then((r) => r.data)
        )

        // Use shorter timeout since this is background work
        const [isAdmin, isCms] = await Promise.all([
          withTimeout(adminPromise, 2000, false as boolean | null),
          withTimeout(cmsPromise, 2000, false as boolean | null),
        ])

        const adminStatus = isAdmin || false
        const cmsStatus = isCms || false

        setIsSuperAdmin(adminStatus)
        setIsCmsUser(cmsStatus)
        setAdminCheckComplete(true)

        // Update cache with fresh values
        setCachedAuthState({
          userId,
          isSuperAdmin: adminStatus,
          isCmsUser: cmsStatus,
        })

        return { isAdmin: adminStatus, isCms: cmsStatus }
      } catch (error) {
        console.error('[AuthProvider] Error in admin/cms checks:', error)
        setIsSuperAdmin(false)
        setIsCmsUser(false)
        setAdminCheckComplete(true)
        return { isAdmin: false, isCms: false }
      }
    },
    [supabase, withTimeout]
  )

  useEffect(() => {
    let isMounted = true
    let hasInitialized = false

    // Process session - now NON-BLOCKING
    async function processSession(
      session: { user: User; access_token?: string | null } | null,
      _source: string,
    ) {
      if (hasInitialized) return
      hasInitialized = true

      if (!isMounted) return

      if (session?.user) {
        setUser(session.user)
        setAccessToken(session.access_token ?? null)
        setLoading(false) // Unblock immediately!

        // Check if we need to revalidate cached admin status
        const shouldRevalidate =
          shouldRevalidateCache(cachedState) || cachedState?.userId !== session.user.id

        if (shouldRevalidate) {
          // Run admin checks in background (doesn't block render)
          runAdminChecks(session.user.id).finally(() => {
            if (isMounted) setIsVerifying(false)
          })
        } else {
          // Cache is fresh, just mark as verified
          setIsVerifying(false)
        }
      } else {
        setUser(null)
        setAccessToken(null)
        setIsSuperAdmin(false)
        setIsCmsUser(false)
        setAdminCheckComplete(true)
        setLoading(false)
        setIsVerifying(false)
        clearCachedAuthState()
      }
    }

    // Set up auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return

      // Handle initial session (first event on page load)
      if (event === 'INITIAL_SESSION' || (event === 'SIGNED_IN' && !hasInitialized)) {
        await processSession(session, event)
        return
      }

      // Handle subsequent sign in/out events
      if (event === 'SIGNED_IN') {
        // Reset adminCheckComplete BEFORE setting user so React batches both
        // into one render. Without this, the render after SIGNED_IN has
        // adminCheckComplete=true (from the prior signed-out processSession call)
        // and isSuperAdmin=false (stale), making isPlatformAdmin=false, which
        // fires the RouterBridge guard and immediately signs the user back out.
        setAdminCheckComplete(false)
        setIsVerifying(true)
        setUser(session?.user || null)
        setAccessToken(session?.access_token ?? null)
        setLoading(false)
        if (session?.user) {
          // Run admin checks in background for new sign-ins
          runAdminChecks(session.user.id).finally(() => {
            if (isMounted) setIsVerifying(false)
          })
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        setAccessToken(null)
        setIsSuperAdmin(false)
        setIsCmsUser(false)
        setAdminCheckComplete(true)
        setLoading(false)
        setIsVerifying(false)
        clearCachedAuthState()
      } else if (event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          setUser(session.user)
          setAccessToken(session.access_token ?? null)
        } else {
          // Token refresh failed - session was invalidated (e.g., signed in elsewhere)
          // Clear state and redirect to login
          console.warn('[AuthProvider] Token refresh failed - session invalidated')
          setUser(null)
          setAccessToken(null)
          setIsSuperAdmin(false)
          setIsCmsUser(false)
          setAdminCheckComplete(true)
          setLoading(false)
          setIsVerifying(false)
          clearCachedAuthState()
        }
      } else if (event === 'USER_UPDATED') {
        if (session?.user) {
          setUser(session.user)
          setAccessToken(session.access_token ?? null)
        }
      }
    })

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [supabase, cachedState, runAdminChecks])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut({ scope: 'local' })
    setUser(null)
    setAccessToken(null)
    setIsSuperAdmin(false)
    setIsCmsUser(false)
    // Keep adminCheckComplete as true - we've "completed" the check (user is signed out)
    // Setting to false would prevent the DashboardGuard from redirecting to login
    setAdminCheckComplete(true)

    // Clear pending requests and auth state
    clearPendingRequests()
    clearCachedAuthState()

    // Clear organization-related storage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('kairos_view_mode')
      localStorage.removeItem('kairos_selected_org_id')
      Cookies.remove('selected_org_id')
    }
  }, [supabase])

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    const {
      data: { user: freshUser },
    } = await supabase.auth.getUser()
    if (freshUser) {
      setUser(freshUser)
    }
  }, [supabase])

  // OPTIMISTIC RENDERING: Always render children immediately
  // No more blocking on initialCheckComplete!
  // The dashboard will show instantly with cached state, and update if needed

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        loading,
        isSuperAdmin,
        isCmsUser,
        adminCheckComplete,
        isVerifying,
        signOut,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
